'use client';

import { useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import Matter from 'matter-js';

const WIDTH = 64;
const HEIGHT = 240;
const SEGMENTS = 10;
const REST_LENGTH = 140;
const SEGMENT_LENGTH = REST_LENGTH / SEGMENTS;
const HANDLE_RADIUS = 6;
const ANCHOR = { x: WIDTH / 2, y: 0 };
const GRAB_RADIUS = HANDLE_RADIUS + 12;
const WALL_THICKNESS = 20;
const RESTITUTION = 0.7;
const DRAG_X_RANGE = Math.min(24, ANCHOR.x - HANDLE_RADIUS);
const DRAG_Y_MIN = 0;
// Capped just past the pull-trigger point (see PULL_TRIGGER_Y below) so the
// cord can't be stretched way out and whip back with too much energy.
const DRAG_Y_MAX = REST_LENGTH + 40;
const INITIAL_SWING = 20;
const MAX_RELEASE_SPEED = 6;
// A real pull-cord needs a decisive tug past its resting length to click the
// switch — a light touch or wobble shouldn't toggle the theme.
const PULL_TRIGGER_Y = ANCHOR.y + REST_LENGTH + 25;

interface RopeState {
  engine: Matter.Engine;
  bodies: Matter.Body[];
  handleBody: Matter.Body;
  dragging: boolean;
  dragPos: { x: number; y: number };
  prevDragPos: { x: number; y: number };
  maxPullY: number;
  armed: boolean;
  audioCtx: AudioContext | null;
}

function RopeScene({ keyTarget }: { keyTarget: HTMLElement }) {
  const { gl } = useThree();
  const lineMaterialRef = useRef<THREE.LineBasicMaterial>(null!);
  const handleMaterialRef = useRef<THREE.MeshBasicMaterial>(null!);
  const positionAttrRef = useRef<THREE.BufferAttribute>(null!);
  const handleMeshRef = useRef<THREE.Mesh>(null!);
  const stateRef = useRef<RopeState | null>(null);

  useEffect(() => {
    const container = gl.domElement;

    const initial = document.documentElement.dataset.theme;
    let theme: 'light' | 'dark' =
      initial === 'light' || initial === 'dark'
        ? initial
        : window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';

    // Physics: a chain of small bodies pinned at the anchor and linked by constraints.
    const engine = Matter.Engine.create();
    engine.gravity.y = 1;
    engine.constraintIterations = 8;

    // Chain links share a non-colliding group so they can't jam against each
    // other and get stuck in the corner up by the anchor/top wall.
    const chainGroup = Matter.Body.nextGroup(true);
    const bodies: Matter.Body[] = [];
    for (let i = 0; i <= SEGMENTS; i++) {
      const swing = (i / SEGMENTS) * INITIAL_SWING;
      const x = ANCHOR.x + swing;
      const y = ANCHOR.y + i * SEGMENT_LENGTH;
      const body = Matter.Bodies.circle(x, y, i === SEGMENTS ? HANDLE_RADIUS : 2, {
        isStatic: i === 0,
        frictionAir: 0.02,
        restitution: RESTITUTION,
        friction: 0,
        collisionFilter: { group: chainGroup },
      });
      bodies.push(body);
    }
    const handleBody = bodies[bodies.length - 1];

    const constraints = bodies.slice(1).map((body, i) =>
      Matter.Constraint.create({
        bodyA: bodies[i],
        bodyB: body,
        length: SEGMENT_LENGTH,
        stiffness: 0.9,
        damping: 0.2,
      }),
    );

    // Walls around the canvas so the chain bounces instead of clipping out.
    // No top wall: it would sit right against the static anchor, and a hard
    // throw could wedge the chain between the two, jittering in place instead
    // of falling. Anything overshooting above y=0 just sails off-canvas and
    // gravity pulls it back into view.
    const wallOptions = { isStatic: true, restitution: RESTITUTION, friction: 0 };
    const walls = [
      Matter.Bodies.rectangle(
        WIDTH / 2,
        HEIGHT + WALL_THICKNESS / 2,
        WIDTH + WALL_THICKNESS * 2,
        WALL_THICKNESS,
        wallOptions,
      ),
      Matter.Bodies.rectangle(
        -WALL_THICKNESS / 2,
        HEIGHT / 2,
        WALL_THICKNESS,
        HEIGHT + WALL_THICKNESS * 2,
        wallOptions,
      ),
      Matter.Bodies.rectangle(
        WIDTH + WALL_THICKNESS / 2,
        HEIGHT / 2,
        WALL_THICKNESS,
        HEIGHT + WALL_THICKNESS * 2,
        wallOptions,
      ),
    ];

    Matter.Composite.add(engine.world, [...bodies, ...constraints, ...walls]);

    function applyColor() {
      const hex = getComputedStyle(document.documentElement)
        .getPropertyValue('--foreground')
        .trim();
      const color = new THREE.Color(hex || '#000000');
      lineMaterialRef.current.color = color;
      handleMaterialRef.current.color = color;
    }
    applyColor();

    function toggleTheme() {
      theme = theme === 'dark' ? 'light' : 'dark';
      document.documentElement.dataset.theme = theme;
      document.cookie = `mish_theme=${theme}; max-age=${60 * 60 * 24 * 365}; path=/`;
      applyColor();
    }

    // A short synthesized click, played the instant a pull crosses the trigger
    // point — like a real pull-switch clicking into place before it releases.
    function playClick() {
      const state = stateRef.current!;
      state.audioCtx ??= new AudioContext();
      const audioCtx = state.audioCtx;
      if (audioCtx.state === 'suspended') audioCtx.resume();
      const duration = 0.02;
      const buffer = audioCtx.createBuffer(1, audioCtx.sampleRate * duration, audioCtx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 8;
      }
      const source = audioCtx.createBufferSource();
      source.buffer = buffer;
      const filter = audioCtx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 3500;
      const gain = audioCtx.createGain();
      gain.gain.value = 0.7;
      source.connect(filter).connect(gain).connect(audioCtx.destination);
      source.start();
    }

    const state: RopeState = {
      engine,
      bodies,
      handleBody,
      dragging: false,
      dragPos: { x: ANCHOR.x, y: ANCHOR.y + REST_LENGTH },
      prevDragPos: { x: ANCHOR.x, y: ANCHOR.y + REST_LENGTH },
      maxPullY: ANCHOR.y + REST_LENGTH,
      armed: false,
      audioCtx: null,
    };
    stateRef.current = state;

    function localPos(e: PointerEvent) {
      const rect = container.getBoundingClientRect();
      return {
        x: ((e.clientX - rect.left) / rect.width) * WIDTH,
        y: ((e.clientY - rect.top) / rect.height) * HEIGHT,
      };
    }

    function clampDrag(x: number, y: number) {
      return {
        x: Math.min(Math.max(x, ANCHOR.x - DRAG_X_RANGE), ANCHOR.x + DRAG_X_RANGE),
        y: Math.min(Math.max(y, DRAG_Y_MIN), DRAG_Y_MAX),
      };
    }

    function onPointerDown(e: PointerEvent) {
      const pos = localPos(e);
      const dx = pos.x - handleBody.position.x;
      const dy = pos.y - handleBody.position.y;
      if (Math.sqrt(dx * dx + dy * dy) > GRAB_RADIUS) return;
      state.dragging = true;
      state.dragPos = clampDrag(pos.x, pos.y);
      state.prevDragPos = state.dragPos;
      state.maxPullY = state.dragPos.y;
      state.armed = false;
      // Make the handle kinematic while dragging so the constraint solver moves the
      // rest of the chain to follow it, instead of also pulling the handle off the cursor.
      Matter.Body.setStatic(handleBody, true);
      container.setPointerCapture(e.pointerId);
    }

    function onPointerMove(e: PointerEvent) {
      if (!state.dragging) return;
      const pos = localPos(e);
      state.prevDragPos = state.dragPos;
      state.dragPos = clampDrag(pos.x, pos.y);
      state.maxPullY = Math.max(state.maxPullY, state.dragPos.y);
      if (!state.armed && state.maxPullY >= PULL_TRIGGER_Y) {
        state.armed = true;
        playClick();
      }
    }

    function onPointerUp(e: PointerEvent) {
      if (!state.dragging) return;
      state.dragging = false;
      container.releasePointerCapture(e.pointerId);
      Matter.Body.setStatic(handleBody, false);
      let vx = state.dragPos.x - state.prevDragPos.x;
      let vy = state.dragPos.y - state.prevDragPos.y;
      const speed = Math.sqrt(vx * vx + vy * vy);
      if (speed > MAX_RELEASE_SPEED) {
        const scale = MAX_RELEASE_SPEED / speed;
        vx *= scale;
        vy *= scale;
      }
      Matter.Body.setVelocity(handleBody, { x: vx, y: vy });
      if (state.maxPullY >= PULL_TRIGGER_Y) {
        playClick();
        toggleTheme();
      }
    }

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', onPointerUp);
    container.addEventListener('pointercancel', onPointerUp);

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleTheme();
      }
    }
    keyTarget.addEventListener('keydown', onKeyDown);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerUp);
      keyTarget.removeEventListener('keydown', onKeyDown);
      Matter.Engine.clear(engine);
      state.audioCtx?.close();
      stateRef.current = null;
    };
  }, [gl, keyTarget]);

  useFrame((_, delta) => {
    const state = stateRef.current;
    if (!state) return;
    const deltaMs = Math.min(delta * 1000, 1000 / 60);

    if (state.dragging) {
      Matter.Body.setPosition(state.handleBody, state.dragPos);
      Matter.Body.setVelocity(state.handleBody, { x: 0, y: 0 });
    }

    Matter.Engine.update(state.engine, deltaMs);

    const positions = positionAttrRef.current;
    state.bodies.forEach((body, i) => positions.setXYZ(i, body.position.x, body.position.y, 0));
    positions.needsUpdate = true;

    handleMeshRef.current.position.set(state.handleBody.position.x, state.handleBody.position.y, 0);
  });

  const linePositions = new Float32Array((SEGMENTS + 1) * 3);

  return (
    <>
      <line>
        <bufferGeometry>
          <bufferAttribute
            ref={positionAttrRef}
            attach='attributes-position'
            args={[linePositions, 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial ref={lineMaterialRef} />
      </line>
      <mesh ref={handleMeshRef}>
        <circleGeometry args={[HANDLE_RADIUS, 24]} />
        <meshBasicMaterial ref={handleMaterialRef} side={THREE.DoubleSide} />
      </mesh>
    </>
  );
}

export default function ThemeRope() {
  const [keyTarget, setKeyTarget] = useState<HTMLDivElement | null>(null);

  return (
    <div
      ref={setKeyTarget}
      role='button'
      tabIndex={0}
      aria-label='Toggle color theme'
      className='fixed top-0 right-0 z-[100]'
      style={{ width: WIDTH, height: HEIGHT, touchAction: 'none' }}
    >
      {keyTarget && (
        <Canvas
          orthographic
          camera={{ left: 0, right: WIDTH, top: 0, bottom: HEIGHT, near: 0.1, far: 100, position: [0, 0, 50] }}
          dpr={[1, 2]}
          gl={{ alpha: true, antialias: true }}
          style={{ width: WIDTH, height: HEIGHT }}
        >
          <RopeScene keyTarget={keyTarget} />
        </Canvas>
      )}
    </div>
  );
}
